import { alpha, Button, LinearProgress, styled, useTheme } from '@mui/material'
import { lime } from '@mui/material/colors'
import {
  DataGridProProps,
  GridRow,
  GridSlotProps,
  GridToolbarProps,
  GridValidRowModel,
  DataGridPro as MUIDataGridPro,
  ToolbarPropsOverrides,
} from '@mui/x-data-grid-pro'
import { omit } from 'ramda'
import { useEffect, useMemo } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { BulkActionToolbar } from './BulkActionToolbar'
import { ServerPaginationFooter, ServerPaginationFooterProps } from './ServerPaginationFooter'
import { ExtendedDataGridProProps } from './types'

function ExtendedDataGridPro<R extends GridValidRowModel>(
  props: DataGridProProps<R> & ExtendedDataGridProProps<R>
) {
  return <MUIDataGridPro<R> {...props} />
}

const StyledDataGridPro = styled(ExtendedDataGridPro)(
  ({ slots, headerPaletteColor, hideBorders, hideCellFocus, onRowClick }) => {
    const theme = useTheme()
    return {
      border: hideBorders ? 'none' : undefined,
      fontSize: theme.typography.body2.fontSize,
      '& .MuiDataGrid-cell': {
        display: 'flex',
        alignItems: 'center',
      },
      '.MuiDataGrid-cell, .MuiDataGrid-columnHeaders': {
        border: hideBorders ? 'none' : undefined,
        overflow: 'hidden',
      },
      '.MuiDataGrid-cell:focus': {
        outline: hideCellFocus ? 'none' : undefined,
      },
      '.MuiDataGrid-row': {
        cursor: !!onRowClick ? 'pointer' : undefined,
      },
      '& .MuiDataGrid-columnHeaders': {
        backgroundColor: headerPaletteColor
          ? theme.palette[headerPaletteColor].main
          : theme.palette.grey[theme.palette.mode === 'light' ? 100 : 900],
        borderRadius: slots?.bulkActions || slots?.toolbar ? 0 : undefined,
        '& .MuiButtonBase-root': {
          color: headerPaletteColor ? theme.palette[headerPaletteColor].contrastText : undefined,
        },
        '& .MuiDataGrid-columnHeader--dragging': {
          color: theme.palette.primary.contrastText,
          backgroundColor: theme.palette.primary.light,
        },
      },
      '& .MuiDataGrid-columnHeaderTitle': {
        color: headerPaletteColor ? theme.palette[headerPaletteColor].contrastText : undefined,
      },
      '& .MuiDataGrid-columnHeader:last-child > .MuiDataGrid-columnSeparator': {
        display: 'none',
      },
      '& .MuiDataGrid-cellCheckbox:focus-within, & .MuiDataGrid-columnHeaderCheckbox:focus-within':
        {
          outline: 'none',
        },
      '& .MuiDataGrid-row.Mui-selected': {
        backgroundColor: alpha(theme.palette.primary.light, 0.1),
        transition: theme.transitions.create('background-color'),
      },
      '& .MuiDataGrid-cell--pinnedLeft': {
        backgroundColor: theme.palette.background.paper,
      },
      '.MuiDataGrid-columnHeaderCheckbox': {
        backgroundColor: `${theme.palette.background.paper} !important`,
      },
      '& .MuiDataGrid-row.highlight': {
        backgroundColor: lime[theme.palette.mode === 'light' ? 100 : 900],
      },
      '& .MuiDataGrid-columnHeader--withRightBorder, & .MuiDataGrid-cell--withRightBorder': {
        borderRight: 'none',
      },
      '& .MuiDataGrid-row.disabled': {
        cursor: 'not-allowed',
        opacity: 0.6,
      },
      '& .MuiDataGrid-row.disabled:hover': {
        backgroundColor: 'unset',
      },
    }
  }
)

/**
 * Extends MUI's DataGridPro in 3 significant ways:
 *
 * 1. Enhanced styling: colored header, paper colored pinned columns
 * 2. Support for a BulkActions component slot
 * 3. Provides accessibility affordances for onRowClick behaviors
 *
 * #BulkActions
 * When specified, this component will appear in the Toolbar area when any rows are selected.
 * If a Toolbar is specified, that component is 'backgrounded' behind the BulkActions.  This
 * is useful if your toolbar affects the table in a way that would conflict with selected rows.
 *
 * For example:
 * Your Toolbar contains a filter, when activating the filter, table rows are dropped.  If a user
 * had previously hand-picked a number of rows, and those rows are now suddenly missing, the user
 * would need to know their selection set changed.  To avoid this potentially confusing situation,
 * specify BulkActions that appear over top of the Toolbar in this case.
 *
 * BulkActions is completely opt-in and will also have the following side-effect:
 * When BulkActions is defined and `paginationMode='server'`, the row count is rendered as text, else
 * a button to deselect all rows is rendered.  We do this b/c MUI's DataGridPro does not support
 * cross-page row selection, however we have a frequent need to support cross-page row selection.
 *
 */

export function DataGridPro<R extends GridValidRowModel>({
  slots = {},
  rowLink,
  onRowButtonClick,
  ...props
}: DataGridProProps<R> & ExtendedDataGridProProps<R>) {
  const theme = useTheme()
  const navigate = useNavigate()
  const bulkActions = useMemo(
    () => (props: GridToolbarProps & ToolbarPropsOverrides) =>
      (
        <BulkActionToolbar
          {...(props as Omit<ExtendedDataGridProProps<R>, 'slots'>)}
          slots={{ toolbar: slots.toolbar, bulkActions: slots.bulkActions }}
        />
      ),
    []
  )

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Enter') {
        navigate(
          // @ts-ignore parentElement does exist, but typescript doesn't believe it
          event.target?.parentElement?.getAttribute('data-to') || ''
        )
      }
    }

    document.addEventListener('keydown', handleKeyDown)

    return () => {
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [])

  const RowComponent = slots.row ?? GridRow
  const rowSlot = rowLink
    ? (params: GridSlotProps['row']) => (
        <Link to={rowLink(params.row as R)} tabIndex={-1}>
          <RowComponent
            style={{ color: theme.palette.text.primary }}
            {...params}
            data-to={rowLink(params.row as R)}
          />
        </Link>
      )
    : onRowButtonClick
    ? (params: GridSlotProps['row']) => (
        // TODO: check styling
        <Button onClick={() => onRowButtonClick(params.row as R)} tabIndex={-1}>
          <RowComponent style={{ color: theme.palette.text.primary }} {...params} />
        </Button>
      )
    : slots.row
    ? slots.row
    : undefined

  const finalSlots = {
    loadingOverlay: () => <LinearProgress sx={{ marginBottom: -0.5 }} />,
    ...omit(['bulkActions'], slots),
    toolbar: slots.bulkActions ? bulkActions : slots.toolbar,
    row: rowSlot,
  }

  const footer = slots.footer
    ? slots.footer
    : props.paginationMode === 'server' && !slots.bulkActions
    ? (props: DataGridProProps & ServerPaginationFooterProps) => (
        <ServerPaginationFooter {...props} />
      )
    : undefined

  if (footer) {
    finalSlots.footer = footer as any
  }

  return (
    <StyledDataGridPro {...(props as DataGridProProps<GridValidRowModel>)} slots={finalSlots} />
  )
}
